import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { type GradeRecord } from './GradeImporterView';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import LocationImporterAPI from 'api/location-importer.api';
import {
  type MaterialCategoryRecord,
  type GradeCategoryRecord,
} from './CategoryManagement/types';
import { CategorySearchSelect } from './CategoryManagement/CategorySearchSelect';
import { AddMaterialCategoryModal } from './CategoryManagement/AddMaterialCategoryModal';
import { AddGradeCategoryModal } from './CategoryManagement/AddGradeCategoryModal';

interface LocationOption {
  locationId: string;
  name: string;
}

interface QualityRow {
  key: string;
  value: string;
}

interface FormData {
  name: string;
  materialCategory: string;
  locationName: string;
  locationId: string | null;
  blend: string;
  dump: string;
  gradeCategory: string;
  specification: string;
  inventory: string;
  specificGravity: string;
  qualities: QualityRow[];
  active: boolean;
}

interface FormErrors {
  name?: string;
  materialCategory?: string;
  locationName?: string;
  inventory?: string;
}

interface Props {
  open: boolean;
  grade: GradeRecord | null;
  onClose: () => void;
  onSave: (updated: Omit<GradeRecord, 'id'>) => Promise<void>;
  materialCategories: MaterialCategoryRecord[];
  gradeCategories: GradeCategoryRecord[];
  onRefetchMaterialCategories: () => Promise<void>;
  onRefetchGradeCategories: () => Promise<void>;
}

const SectionLabel = ({ label }: { label: string }) => (
  <Box
    sx={{
      mb: 1.5,
      pb: 0.5,
      borderBottom: '1px solid',
      borderColor: (t) => alpha(t.palette.divider, 0.6),
    }}
  >
    <Typography
      variant="caption"
      fontWeight={700}
      color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
    >
      {label}
    </Typography>
  </Box>
);

export const EditGradeModal = ({
  open,
  grade,
  onClose,
  onSave,
  materialCategories,
  gradeCategories,
  onRefetchMaterialCategories,
  onRefetchGradeCategories,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const locationApi = useRef(new LocationImporterAPI()).current;
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [addMaterialCategoryOpen, setAddMaterialCategoryOpen] = useState(false);
  const [addMaterialCategoryPrefill, setAddMaterialCategoryPrefill] =
    useState('');
  const [addGradeCategoryOpen, setAddGradeCategoryOpen] = useState(false);
  const [addGradeCategoryPrefill, setAddGradeCategoryPrefill] = useState('');
  const [form, setForm] = useState<FormData>({
    name: '',
    materialCategory: '',
    locationName: '',
    locationId: null,
    blend: '',
    dump: '',
    gradeCategory: '',
    specification: '',
    inventory: '',
    specificGravity: '',
    qualities: [],
    active: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLocationsLoading(true);
    locationApi
      .getLocations()
      .then((data) => {
        setLocations(
          data
            .filter((l) => l.locationType?.toLowerCase() === 'loading')
            .map((l) => ({ locationId: l.locationId, name: l.name }))
        );
      })
      .catch(() => {})
      .finally(() => {
        setLocationsLoading(false);
      });
  }, [open]);

  useEffect(() => {
    if (grade) {
      setForm({
        name: grade.name ?? '',
        materialCategory: grade.materialCategory ?? '',
        locationName: grade.locationName ?? '',
        locationId: grade.locationId,
        blend: grade.blend ?? '',
        dump: grade.dump ?? '',
        gradeCategory: grade.gradeCategory ?? '',
        specification: grade.specification ?? '',
        inventory: grade.inventory !== null ? String(grade.inventory) : '',
        specificGravity:
          grade.specificGravity !== null ? String(grade.specificGravity) : '',
        qualities: Object.entries(grade.qualities ?? {}).map(
          ([key, value]) => ({
            key,
            value: String(value),
          })
        ),
        active: grade.active,
      });
      setErrors({});
    }
  }, [grade]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = t('grade_importer_required');
    if (!form.materialCategory.trim())
      next.materialCategory = t('grade_importer_required');
    if (!form.locationName.trim())
      next.locationName = t('grade_importer_required');
    if (!form.inventory.trim()) next.inventory = t('grade_importer_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const qualitiesPayload: Record<string, number> = {};
    for (const row of form.qualities) {
      const k = row.key.trim().toLowerCase();
      const num = parseFloat(row.value);
      if (k && !isNaN(num)) {
        qualitiesPayload[k] = num;
      }
    }

    const updated: Omit<GradeRecord, 'id'> = {
      name: form.name.trim(),
      materialCategory: form.materialCategory.trim(),
      locationName: form.locationName.trim(),
      locationId: form.locationId,
      blend: form.blend.trim(),
      dump: form.dump.trim(),
      gradeCategory: form.gradeCategory.trim() || null,
      specification: form.specification.trim(),
      inventory:
        form.inventory.trim() !== '' && !isNaN(parseFloat(form.inventory))
          ? parseFloat(form.inventory)
          : null,
      specificGravity:
        form.specificGravity.trim() !== '' &&
        !isNaN(parseFloat(form.specificGravity))
          ? parseFloat(form.specificGravity)
          : null,
      qualities: qualitiesPayload,
      active: form.active,
    };

    setSubmitting(true);
    try {
      await onSave(updated);
    } catch {
      // error already toasted by parent; modal stays open
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    (key: keyof Omit<FormData, 'qualities' | 'active'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const addQualityRow = () => {
    setForm((prev) => ({
      ...prev,
      qualities: [...prev.qualities, { key: '', value: '' }],
    }));
  };

  const removeQualityRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      qualities: prev.qualities.filter((_, i) => i !== index),
    }));
  };

  const updateQualityRow = (
    index: number,
    field: 'key' | 'value',
    val: string
  ) => {
    setForm((prev) => {
      const updated = [...prev.qualities];
      updated[index] = { ...updated[index], [field]: val };
      return { ...prev, qualities: updated };
    });
  };

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.15),
            backgroundImage: 'none',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            pt: 2.5,
            pb: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {t('grade_importer_edit_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {grade?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={submitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* ── Single scrollable body ── */}
        <DialogContent sx={{ px: 3, pt: 1, pb: 2, overflowY: 'auto', flex: 1 }}>
          {/* Grade Info */}
          <SectionLabel label={t('grade_importer_section_info')} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_name')}
                fullWidth
                size="small"
                value={form.name}
                onChange={field('name')}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CategorySearchSelect
                label={t('grade_importer_field_material')}
                codes={materialCategories.map((m) => m.enumValue)}
                getLabel={(code) => code}
                value={form.materialCategory || null}
                onChange={(code) => {
                  setForm((prev) => ({
                    ...prev,
                    materialCategory: code ?? '',
                  }));
                  if (code) {
                    setErrors((prev) => ({
                      ...prev,
                      materialCategory: undefined,
                    }));
                  }
                }}
                addNewLabel={t('grade_importer_add_new_material_category')}
                onAddNew={(searchText) => {
                  setAddMaterialCategoryPrefill(searchText);
                  setAddMaterialCategoryOpen(true);
                }}
                error={errors.materialCategory}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={locations}
                getOptionLabel={(o) => o.name}
                loading={locationsLoading}
                value={
                  locations.find((l) => l.locationId === form.locationId) ??
                  null
                }
                onChange={(_e, selected) => {
                  setForm((prev) => ({
                    ...prev,
                    locationName: selected?.name ?? '',
                    locationId: selected?.locationId ?? null,
                  }));
                  if (selected) {
                    setErrors((prev) => ({ ...prev, locationName: undefined }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('grade_importer_field_location_name')}
                    size="small"
                    error={!!errors.locationName}
                    helperText={errors.locationName}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {locationsLoading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_blend')}
                fullWidth
                size="small"
                value={form.blend}
                onChange={field('blend')}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_dump')}
                fullWidth
                size="small"
                value={form.dump}
                onChange={field('dump')}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CategorySearchSelect
                label={t('grade_importer_field_category')}
                codes={gradeCategories.map((g) => g.gradeCategory)}
                getLabel={(code) => {
                  const rec = gradeCategories.find(
                    (g) => g.gradeCategory === code
                  );
                  return rec
                    ? `${code} (${rec.minGrade}–${rec.maxGrade})`
                    : code;
                }}
                value={form.gradeCategory || null}
                onChange={(code) => {
                  setForm((prev) => ({ ...prev, gradeCategory: code ?? '' }));
                }}
                addNewLabel={t('grade_importer_add_new_grade_category')}
                onAddNew={(searchText) => {
                  setAddGradeCategoryPrefill(searchText);
                  setAddGradeCategoryOpen(true);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_specification')}
                fullWidth
                size="small"
                value={form.specification}
                onChange={field('specification')}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          </Grid>

          {/* Metrics */}
          <SectionLabel label={t('grade_importer_section_metrics')} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_inventory')}
                fullWidth
                size="small"
                type="number"
                value={form.inventory}
                onChange={field('inventory')}
                error={!!errors.inventory}
                helperText={errors.inventory}
                inputProps={{
                  step: 'any',
                  onWheel: (e) => {
                    (e.target as HTMLInputElement).blur();
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('grade_importer_field_specific_gravity')}
                fullWidth
                size="small"
                type="number"
                value={form.specificGravity}
                onChange={field('specificGravity')}
                inputProps={{
                  step: 'any',
                  onWheel: (e) => {
                    (e.target as HTMLInputElement).blur();
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Qualities */}
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 0.5,
                borderBottom: '1px solid',
                borderColor: (t) => alpha(t.palette.divider, 0.6),
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
              >
                {t('grade_importer_section_qualities')}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                onClick={addQualityRow}
                variant="outlined"
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  borderRadius: '6px',
                  py: 0.3,
                  px: 1.2,
                  minWidth: 0,
                }}
              >
                {t('grade_importer_quality_add_row')}
              </Button>
            </Box>

            {form.qualities.length === 0 ? (
              <Box
                sx={{
                  py: 3,
                  mb: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: (t) => alpha(t.palette.divider, 0.5),
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                  },
                }}
                onClick={addQualityRow}
              >
                <AddIcon
                  sx={{ fontSize: 20, color: 'text.disabled', mb: 0.5 }}
                />
                <Typography
                  variant="body2"
                  color="text.disabled"
                  sx={{ fontSize: '0.8rem' }}
                >
                  {t('grade_importer_quality_empty')}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.divider, 0.5),
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 36px',
                    px: 1.5,
                    py: 0.8,
                    bgcolor: (t) => alpha(t.palette.action.hover, 0.04),
                    borderBottom: '1px solid',
                    borderColor: (t) => alpha(t.palette.divider, 0.4),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    {t('grade_importer_quality_col_element')}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                  >
                    {t('grade_importer_quality_col_value')}
                  </Typography>
                  <Box />
                </Box>

                {form.qualities.map((row, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 36px',
                      alignItems: 'center',
                      px: 1.5,
                      py: 0.75,
                      gap: 1.5,
                      borderBottom:
                        idx < form.qualities.length - 1 ? '1px solid' : 'none',
                      borderColor: (t) => alpha(t.palette.divider, 0.3),
                      '&:hover': {
                        bgcolor: (t) => alpha(t.palette.action.hover, 0.04),
                      },
                    }}
                  >
                    <TextField
                      placeholder={t('grade_importer_quality_key_placeholder')}
                      size="small"
                      value={row.key}
                      onChange={(e) => {
                        updateQualityRow(idx, 'key', e.target.value);
                      }}
                      inputProps={{ maxLength: 50 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                        },
                      }}
                    />
                    <TextField
                      placeholder={t(
                        'grade_importer_quality_value_placeholder'
                      )}
                      size="small"
                      type="number"
                      value={row.value}
                      onChange={(e) => {
                        updateQualityRow(idx, 'value', e.target.value);
                      }}
                      inputProps={{
                        step: 'any',
                        onWheel: (e) => {
                          (e.target as HTMLInputElement).blur();
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { fontSize: '0.82rem' },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        removeQualityRow(idx);
                      }}
                      sx={{ color: 'error.main', flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>

          {/* Status */}
          <SectionLabel label={t('grade_importer_section_status')} />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, active: e.target.checked }));
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color="text.primary">
                {t('grade_importer_field_active')}
              </Typography>
            }
          />
        </DialogContent>

        {/* ── Actions ── */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={submitting}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('grade_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('grade_importer_save_changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <AddMaterialCategoryModal
        open={addMaterialCategoryOpen}
        initialCode={addMaterialCategoryPrefill}
        onClose={() => {
          setAddMaterialCategoryOpen(false);
        }}
        onCreated={(created) => {
          setAddMaterialCategoryOpen(false);
          setForm((prev) => ({ ...prev, materialCategory: created.enumValue }));
          setErrors((prev) => ({ ...prev, materialCategory: undefined }));
          void onRefetchMaterialCategories();
        }}
      />
      <AddGradeCategoryModal
        open={addGradeCategoryOpen}
        initialCode={addGradeCategoryPrefill}
        onClose={() => {
          setAddGradeCategoryOpen(false);
        }}
        onCreated={(created) => {
          setAddGradeCategoryOpen(false);
          setForm((prev) => ({
            ...prev,
            gradeCategory: created.gradeCategory,
          }));
          void onRefetchGradeCategories();
        }}
      />
    </>
  );
};
